import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { AdminThemeProvider } from "@/admin/AdminTheme";
import { Layout } from "@/components/Layout";
import { RequireAuth, RequireAdmin } from "@/components/common";
import { GoogleAuthHandler } from "@/components/GoogleAuth";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Category from "@/pages/Category";
import Product from "@/pages/Product";
import Search from "@/pages/Search";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import Gifts from "@/pages/Gifts";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Points from "@/pages/Points";
import Wishlist from "@/pages/Wishlist";
import Profile from "@/pages/Profile";
import Shortlists from "@/pages/Shortlists";
import ShortlistDetail from "@/pages/ShortlistDetail";
import PublicShortlist from "@/pages/PublicShortlist";
import PolicyPage from "@/pages/PolicyPage";

import { AdminLayout } from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminOrders from "@/admin/AdminOrders";
import AdminOrderDetail from "@/admin/AdminOrderDetail";
import AdminProducts from "@/admin/AdminProducts";
import AdminProductDetail from "@/admin/AdminProductDetail";
import AdminProductEditor from "@/admin/AdminProductEditor";
import AdminInventory from "@/admin/AdminInventory";
import AdminPromotions from "@/admin/AdminPromotions";
import AdminReviews from "@/admin/AdminReviews";
import AdminCustomers from "@/admin/AdminCustomers";
import AdminCustomerDetail from "@/admin/AdminCustomerDetail";
import AdminMore from "@/admin/AdminMore";
import AdminCatalog from "@/admin/AdminCatalog";
import AdminStockReport from "@/admin/AdminStockReport";

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "DM Sans" } }} />
          <GoogleAuthHandler />
          <Routes>
            {/* Standalone */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/shortlist/share/:slug" element={<PublicShortlist />} />

            {/* Storefront */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/category/:id" element={<Category />} />
              <Route path="/product/:id" element={<Product />} />
              <Route path="/search" element={<Search />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/collection/:id" element={<CollectionDetail />} />
              <Route path="/gifts" element={<Gifts />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/policy/:slug" element={<PolicyPage />} />
              <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
              <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
              <Route path="/order/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
              <Route path="/points" element={<RequireAuth><Points /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
              <Route path="/shortlists" element={<RequireAuth><Shortlists /></RequireAuth>} />
              <Route path="/shortlist/:id" element={<RequireAuth><ShortlistDetail /></RequireAuth>} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<RequireAdmin><AdminThemeProvider><AdminLayout /></AdminThemeProvider></RequireAdmin>}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="order/:id" element={<AdminOrderDetail />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="product/new" element={<AdminProductEditor />} />
              <Route path="product/:id" element={<AdminProductDetail />} />
              <Route path="product/:id/edit" element={<AdminProductEditor />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customer/:id" element={<AdminCustomerDetail />} />
              <Route path="more" element={<AdminMore />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="stock-report" element={<AdminStockReport />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
